import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  cloudAdminListAuditLogs,
  cloudHealth,
  cloudListAuditLogs,
  ensureCloudSession,
  getCloudToken,
  mapApiAuditLog,
} from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { auditLogs as seed, type AuditEntry } from "./mock";

interface AuditState {
  list: AuditEntry[];
  cloudReady: boolean;
  syncing: boolean;
  add: (log: AuditEntry) => void;
  refreshFromCloud: () => Promise<void>;
}

const AuditContext = createContext<AuditState | null>(null);

function initialLogs(): AuditEntry[] {
  return seed.map((l) => ({ ...l }));
}

export function AuditProvider({ children }: { children: ReactNode }) {
  const { email, isAuthenticated, role } = useAuth();
  const [list, setList] = useState<AuditEntry[]>(initialLogs);
  const [cloudReady, setCloudReady] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refreshFromCloud = useCallback(async () => {
    if (!role) return;
    setSyncing(true);
    try {
      const ok = await cloudHealth();
      if (!ok) {
        setCloudReady(false);
        return;
      }
      if (!getCloudToken() && email) {
        try {
          const sess = await ensureCloudSession(email);
          if (sess.ok === false) throw new Error(sess.message);
        } catch {
          setCloudReady(false);
          return;
        }
      }
      if (!getCloudToken()) {
        setCloudReady(false);
        return;
      }
      const { items } =
        role === "platform_admin"
          ? await cloudAdminListAuditLogs(200)
          : await cloudListAuditLogs();
      // Replace — empty mock seed must not hide / mix with cloud rows.
      setList((items || []).map((l) => mapApiAuditLog(l)));
      setCloudReady(true);
    } catch {
      setCloudReady(false);
    } finally {
      setSyncing(false);
    }
  }, [email, role]);

  useEffect(() => {
    if (!isAuthenticated || !role) return;
    void refreshFromCloud();
    const t = window.setInterval(() => void refreshFromCloud(), 15000);
    return () => window.clearInterval(t);
  }, [isAuthenticated, role, refreshFromCloud]);

  const value = useMemo<AuditState>(
    () => ({
      list,
      cloudReady,
      syncing,
      add: (log) => setList((prev) => [log, ...prev]),
      refreshFromCloud,
    }),
    [list, cloudReady, syncing, refreshFromCloud],
  );

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>;
}

export function useAudit() {
  const ctx = useContext(AuditContext);
  if (!ctx) {
    throw new Error("useAudit must be used within AuditProvider");
  }
  return ctx;
}
